import Link from 'next/link';
import Image from 'next/image';
import { display } from '../../lib/fonts';
import { alert } from '../../lib/ui';

// Split screen used by login, sign-up and OTP verification.
// Left panel (desktop only) explains the value; right panel holds the form.
export default function AuthLayout({ sideTitle, sideText, benefits = [], image, imageAlt = '', children }) {
  return (
    <main className="min-h-[calc(100vh-4rem)] bg-white dark:bg-gray-950 lg:grid lg:grid-cols-2">
      <aside className="hidden lg:flex flex-col justify-center border-r border-slate-200 dark:border-gray-800 bg-slate-50 dark:bg-gray-900 px-12 xl:px-20 py-14">
        <div className="max-w-lg">
          <h2 className={`${display.className} text-4xl font-bold leading-tight tracking-[-0.025em] text-slate-900 dark:text-white text-balance`}>
            {sideTitle}
          </h2>
          {sideText && <p className="mt-4 text-lg leading-relaxed text-slate-600 dark:text-slate-400">{sideText}</p>}
          <ul className="mt-8 space-y-3">
            {benefits.map((item) => (
              <li key={item} className="flex items-start gap-3 text-base text-slate-700 dark:text-slate-300">
                <svg className="mt-1 h-4 w-4 flex-shrink-0 text-teal-600 dark:text-customBlue" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5} aria-hidden="true">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                </svg>
                {item}
              </li>
            ))}
          </ul>
          {image && (
            <div className="mt-10 rounded-2xl border border-slate-200 dark:border-gray-800 bg-white dark:bg-gray-950 p-6">
              <Image src={image} alt={imageAlt} width={400} height={400} className="h-auto w-full max-h-[300px] object-contain" priority />
            </div>
          )}
        </div>
      </aside>

      <section className="flex items-start lg:items-center justify-center px-4 sm:px-6 py-10 sm:py-14 lg:px-12">
        <div className="w-full max-w-md">{children}</div>
      </section>
    </main>
  );
}

// Two-option segmented control for choosing the account type.
export function RoleToggle({ role, onChange }) {
  const options = [
    { value: 'job-seeker', label: 'Job Seeker' },
    { value: 'referrer', label: 'Referrer' },
  ];
  return (
    <div role="tablist" aria-label="Account type" className="grid grid-cols-2 rounded-xl bg-slate-100 dark:bg-gray-900 p-1">
      {options.map((o) => (
        <button
          key={o.value}
          type="button"
          role="tab"
          aria-selected={role === o.value}
          onClick={() => onChange(o.value)}
          className={`min-h-[44px] rounded-lg text-sm font-semibold transition ${
            role === o.value
              ? 'bg-white dark:bg-gray-700 text-slate-900 dark:text-white shadow-sm'
              : 'text-slate-500 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white'
          }`}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}

const ErrorIcon = () => (
  <svg className="mt-0.5 h-5 w-5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} aria-hidden="true">
    <path strokeLinecap="round" strokeLinejoin="round" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
  </svg>
);
const OkIcon = () => (
  <svg className="mt-0.5 h-5 w-5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} aria-hidden="true">
    <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
  </svg>
);

export function Notice({ type = 'error', children }) {
  if (!children) return null;
  return (
    <div role={type === 'error' ? 'alert' : 'status'} className={alert[type]}>
      {type === 'error' ? <ErrorIcon /> : <OkIcon />}
      <p>{children}</p>
    </div>
  );
}

export function Divider({ children }) {
  return (
    <div className="flex items-center gap-3">
      <div className="h-px flex-1 bg-slate-200 dark:bg-gray-800" />
      <span className="text-xs text-slate-500 dark:text-slate-400">{children}</span>
      <div className="h-px flex-1 bg-slate-200 dark:bg-gray-800" />
    </div>
  );
}
